import LogOutBtn from '@components/atoms/navbar/LogOutBtn';
import { useProfile, useVersion } from '@hooks/api';
import { useLoadingModal } from '@hooks/useLoadingModal';
import {
    cn,
    HouseOutlineIcon,
    MyPortfolioIcon,
    ProfileIcon,
    TransactionHistoryIcon,
} from '@moreharvest/component-library';
import { navClick } from '@services/analytics';
import {
    AnalyticsDestination,
    AnalyticsNavLocation,
    AnalyticsScreenName,
    ProfileResponse,
} from 'mh-client';
import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import ContextMenuBase from '../../atoms/navbar/ContextMenuBase';
import ContextMenuTitle from '../../molecules/navbar/ContextMenuTitle';
import { ContextMenuContext } from './ContextMenuContext';
import VersionInfo from './VersionInfo';

/** Drawer navigation fires `nav_click` from the menu surface (screen_name=menu). */
const fireNavClick = (destination: string) =>
    navClick({
        screen_name: AnalyticsScreenName.getInstance().MENU,
        nav_location: AnalyticsNavLocation.getInstance().SIDE_MENU,
        destination,
    });

const menuLinksWrapperClasses = (isOpen: boolean) =>
    cn(
        'bg-neutral-50 dark:bg-neutral-50-dark',
        'py-4 lg:px-4 pb-2',
        'lg:border-b-1 lg:border-x-1 lg:border-neutral-200 dark:lg:border-neutral-200-dark',
        'lg:rounded-b-lg lg:w-[13.25rem]',
        'flex-col gap-y-4',
        isOpen ? 'flex lg:absolute top-[54px] z-50' : 'flex lg:hidden'
    );

const ContextMenu = ({ cytag }: { cytag?: string }) => {
    const [isOpen, setIsOpen] = useState(false);
    const ref = useRef<HTMLUListElement>(null);

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (
                isOpen &&
                ref.current &&
                !ref.current.contains(event.target as Node)
            ) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    return (
        <ContextMenuContext.Provider value={{ isOpen, setIsOpen }}>
            <ul className="flex flex-col relative h-fit" ref={ref}>
                <li
                    className="lg:pl-4 mb-2 lg:mb-0 lg:cursor-pointer pointer-events-none lg:pointer-events-auto "
                    onClick={() => {
                        if (!isOpen) {
                            navClick({
                                nav_location:
                                    AnalyticsNavLocation.getInstance()
                                        .SIDE_MENU,
                                destination:
                                    AnalyticsDestination.getInstance().MENU,
                            });
                        }
                        setIsOpen(!isOpen);
                    }}
                    id="topNav_dropdown_menu"
                >
                    <ContextMenuTitle />
                </li>
                <li>
                    <ContextLinks cytag={cytag} />
                </li>
            </ul>
        </ContextMenuContext.Provider>
    );
};

const logOutBtnWrapClasses = 'hidden lg:block mt-4';

export const VerifiedContextLinks = ({ cytag }: { cytag?: string }) => {
    const { isOpen } = useContext(ContextMenuContext);
    const intl = useIntl();

    const { data, isLoading } = useVersion({
        enabled: import.meta.env.VITE_ENV !== 'production',
    });

    useLoadingModal(isLoading);
    return (
        <ul className={menuLinksWrapperClasses(isOpen)}>
            <ContextMenuBase
                to={'/'}
                label={intl.formatMessage({ id: 'screen_home' })}
                leftIcon={
                    <HouseOutlineIcon className="w-6 h-6 stroke-neutral-800 dark:stroke-neutral-800-dark" />
                }
                id={'topNav_link_Home'}
                cy={`${cytag}-Home`}
                onClick={() =>
                    fireNavClick(AnalyticsDestination.getInstance().HOME)
                }
            />
            <ContextMenuBase
                to={'/profile'}
                label={intl.formatMessage({ id: 'screen_profile' })}
                leftIcon={
                    <ProfileIcon className="w-6 h-6 fill-neutral-800 dark:fill-neutral-800-dark" />
                }
                id="topNav_link_Profile"
                cy={`${cytag}-Profile`}
                onClick={() =>
                    fireNavClick(AnalyticsDestination.getInstance().PROFILE)
                }
            />
            <ContextMenuBase
                to={'/profile/portfolio'}
                label={intl.formatMessage({ id: 'screen_my_portfolio' })}
                leftIcon={
                    <MyPortfolioIcon className="w-6 h-6 stroke-neutral-800 dark:stroke-neutral-800-dark" />
                }
                id="topNav_link_MyPortfolio"
                cy={`${cytag}-MyPortfolio`}
                onClick={() =>
                    fireNavClick(
                        AnalyticsDestination.getInstance().MY_PORTFOLIO
                    )
                }
            />
            <ContextMenuBase
                to={'/profile/transactions'}
                label={intl.formatMessage({ id: 'screen_transaction_history' })}
                leftIcon={
                    <TransactionHistoryIcon className="w-6 h-6 stroke-neutral-800 dark:stroke-neutral-800-dark" />
                }
                id="topNav_link_TransactionHistory"
                cy={`${cytag}-TransactionHistory`}
                onClick={() =>
                    fireNavClick(
                        AnalyticsDestination.getInstance().TRANSACTION_HISTORY
                    )
                }
            />
            {data && (
                <VersionInfo
                    env={import.meta.env.VITE_ENV}
                    version={data.version}
                    branch={import.meta.env.VITE_GIT_BRANCH}
                />
            )}
            <li className={logOutBtnWrapClasses}>
                <LogOutBtn
                    screenName={AnalyticsScreenName.getInstance().MENU}
                />
            </li>
        </ul>
    );
};

export const UnverifiedContextLinks = ({ cytag }: { cytag?: string }) => {
    const { isOpen } = useContext(ContextMenuContext);
    const intl = useIntl();
    return (
        <ul className={menuLinksWrapperClasses(isOpen)}>
            <ContextMenuBase
                to={'/'}
                label={intl.formatMessage({ id: 'screen_home' })}
                leftIcon={
                    <HouseOutlineIcon className="w-6 h-6 stroke-neutral-800 dark:stroke-neutral-800-dark" />
                }
                id={'topNav_link_Home'}
                data-cy={`${cytag}-Home`}
                onClick={() =>
                    fireNavClick(AnalyticsDestination.getInstance().HOME)
                }
            />
            <ContextMenuBase
                to={'/profile'}
                label={intl.formatMessage({ id: 'screen_profile' })}
                leftIcon={
                    <ProfileIcon className="w-6 h-6 fill-neutral-800 dark:fill-neutral-800-dark" />
                }
                id="topNav_link_Profile"
                data-cy={`${cytag}-Profile`}
                onClick={() =>
                    fireNavClick(AnalyticsDestination.getInstance().PROFILE)
                }
            />
            <li className={logOutBtnWrapClasses}>
                <LogOutBtn
                    screenName={AnalyticsScreenName.getInstance().MENU}
                />
            </li>
        </ul>
    );
};

export const AnonymousContextLinks = ({ cytag }: { cytag?: string }) => {
    const { isOpen } = useContext(ContextMenuContext);

    return (
        <ul className={menuLinksWrapperClasses(isOpen)}>
            <ContextMenuBase
                to={'/'}
                label={'Home'}
                leftIcon={
                    <HouseOutlineIcon className="w-6 h-6 stroke-neutral-800 dark:stroke-neutral-800-dark" />
                }
                data-cy={`${cytag}-Home`}
            />

            <li className={logOutBtnWrapClasses}>
                <LogOutBtn />
            </li>
        </ul>
    );
};

export const ContextLinks = ({ cytag }: { cytag?: string }) => {
    const { data: userProfile, isLoading } = useProfile();

    const renderLoggedStatus = useCallback(
        (userProfile: ProfileResponse) => {
            switch (userProfile.accountStatus?.name) {
                case 'Complete':
                    return <VerifiedContextLinks cytag={cytag} />;
                case 'None':
                case 'KycVerified':
                default:
                    return <UnverifiedContextLinks cytag={cytag} />;
            }
        },
        [cytag]
    );

    useLoadingModal(isLoading);

    return userProfile ? <>{renderLoggedStatus(userProfile)}</> : <></>;
};

export default ContextMenu;
